const PLATFORM_KEY = 'ADMIN'
const VISITOR_KEY = 'qcyy_admin_visitor_id'
const SESSION_KEY = 'qcyy_admin_session_id'
const API_SESSION_ENDPOINT = '/api/tracking/session'
const API_EVENT_BATCH_ENDPOINT = '/api/tracking/events/batch'
const EVENT_BATCH_SIZE = 20
const EVENT_FLUSH_DELAY_MS = 1000

let currentRoute = null
let currentRouteEnteredAt = 0
let currentUser = null
let heartbeatTimer = null
let eventFlushTimer = null
let pendingEvents = []

function randomId(prefix) {
    const cryptoApi = window.crypto || window.msCrypto
    const bytes = new Uint8Array(12)
    if (cryptoApi && cryptoApi.getRandomValues) {
        cryptoApi.getRandomValues(bytes)
        return prefix + Array.from(bytes).map((item) => item.toString(16).padStart(2, '0')).join('')
    }
    return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 12)
}

function getVisitorId() {
    let visitorId = window.localStorage.getItem(VISITOR_KEY)
    if (!visitorId) {
        visitorId = randomId('adv_')
        window.localStorage.setItem(VISITOR_KEY, visitorId)
    }
    return visitorId
}

function getSessionId() {
    let sessionId = window.sessionStorage.getItem(SESSION_KEY)
    if (!sessionId) {
        sessionId = randomId('ads_')
        window.sessionStorage.setItem(SESSION_KEY, sessionId)
    }
    return sessionId
}

function nowDuration(startAt) {
    return startAt ? Math.max(0, Date.now() - startAt) : 0
}

function currentPagePath() {
    return window.location.hash || window.location.pathname || '/'
}

function routeTitle(route) {
    return (route && route.meta && route.meta.title) || document.title || ''
}

function deviceContext() {
    return {
        deviceType: window.innerWidth <= 768 ? 'mobile' : 'desktop',
        osName: navigator.platform || '',
        browserName: navigator.userAgent || '',
        networkType: navigator.connection && (navigator.connection.effectiveType || navigator.connection.type) || '',
    }
}

function actorContext() {
    if (!currentUser) {
        return {}
    }
    return {
        actorType: 'ADMIN_ACCOUNT',
        actorId: currentUser.id,
        actorName: currentUser.displayName || currentUser.username || '',
        actorPhone: currentUser.phone || '',
        actorRole: currentUser.primaryRoleCode || currentUser.roleCode || '',
    }
}

function basePayload() {
    return {
        platformKey: PLATFORM_KEY,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        pagePath: currentPagePath(),
        pageTitle: document.title || '',
        ...actorContext(),
        ...deviceContext(),
    }
}

function postTracking(url, payload, useBeacon = false) {
    const body = JSON.stringify(payload)
    if (useBeacon && navigator.sendBeacon) {
        const blob = new Blob([body], {type: 'application/json'})
        navigator.sendBeacon(url, blob)
        return
    }
    window.fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body,
        keepalive: useBeacon,
    }).catch(() => {
        // 静默观测不能影响后台使用。
    })
}

function recordSession(useBeacon = false) {
    postTracking(API_SESSION_ENDPOINT, {
        platformKey: PLATFORM_KEY,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        entryPath: currentRoute ? currentRoute.fullPath : currentPagePath(),
        entryTitle: routeTitle(currentRoute),
        refererUrl: document.referrer || '',
        durationMs: nowDuration(currentRouteEnteredAt),
        ...actorContext(),
        ...deviceContext(),
    }, useBeacon)
}

/**
 * 刷新当前待发送事件；单次最多20条，避免高频交互为每条事件单独创建HTTP请求。
 */
export function flushTrackingEvents(useBeacon = false) {
    if (eventFlushTimer) {
        window.clearTimeout(eventFlushTimer)
        eventFlushTimer = null
    }
    while (pendingEvents.length) {
        const events = pendingEvents.splice(0, EVENT_BATCH_SIZE)
        postTracking(API_EVENT_BATCH_ENDPOINT, {events}, useBeacon)
    }
}

function scheduleEventFlush() {
    if (eventFlushTimer) {
        return
    }
    eventFlushTimer = window.setTimeout(() => flushTrackingEvents(), EVENT_FLUSH_DELAY_MS)
}

export function trackEvent(eventType, eventName, extra = {}) {
    pendingEvents.push({
        ...basePayload(),
        eventType,
        eventName,
        action: extra.action || eventName,
        bizType: extra.bizType || '',
        bizId: extra.bizId || null,
        resultStatus: extra.resultStatus || 'SUCCESS',
        errorCode: extra.errorCode || '',
        errorMessage: extra.errorMessage || '',
        durationMs: extra.durationMs || null,
        extra,
    })
    if (pendingEvents.length >= EVENT_BATCH_SIZE) {
        flushTrackingEvents()
        return
    }
    scheduleEventFlush()
}

function trackRouteLeave() {
    if (!currentRoute) {
        return
    }
    trackEvent('PAGE_LEAVE', '后台页面离开', {
        pageName: routeTitle(currentRoute),
        fromPath: currentRoute.fullPath,
        durationMs: nowDuration(currentRouteEnteredAt),
    })
}

function trackRouteView(route, from) {
    currentRoute = route
    currentRouteEnteredAt = Date.now()
    trackEvent('PAGE_VIEW', '后台页面访问', {
        pageName: routeTitle(route),
        routeName: route.name || '',
        routePath: route.fullPath,
        fromPath: from && from.fullPath || '',
    })
}

function describeElement(element) {
    const text = (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim()
    return {
        tagName: element.tagName,
        text: text.slice(0, 80),
        id: element.id || '',
        className: typeof element.className === 'string' ? element.className.slice(0, 160) : '',
        type: element.getAttribute('type') || '',
        href: element.getAttribute('href') || '',
        ariaLabel: element.getAttribute('aria-label') || '',
    }
}

function installClickTracking() {
    document.addEventListener('click', (event) => {
        const target = event.target && event.target.closest && event.target.closest(
            'button,a,[role="button"],.el-button,.el-tag,.el-menu-item,.el-dropdown-menu__item'
        )
        if (!target) {
            return
        }
        trackEvent('UI_CLICK', '后台界面点击', describeElement(target))
    }, true)
}

export function getTrackingHeaders() {
    const payload = basePayload()
    return {
        'X-QCYY-Platform': PLATFORM_KEY,
        'X-QCYY-Visitor-Id': payload.visitorId,
        'X-QCYY-Session-Id': payload.sessionId,
        'X-QCYY-Page-Path': encodeURIComponent(payload.pagePath || ''),
        'X-QCYY-Page-Title': encodeURIComponent(payload.pageTitle || ''),
        'X-QCYY-Actor-Type': payload.actorType || '',
        'X-QCYY-Actor-Id': payload.actorId || '',
        'X-QCYY-Actor-Name': encodeURIComponent(payload.actorName || ''),
        'X-QCYY-Actor-Phone': payload.actorPhone || '',
        'X-QCYY-Actor-Role': payload.actorRole || '',
    }
}

export function trackApiResult(config, resultStatus, detail = {}) {
    const url = config && config.url || ''
    if (!url || url.includes('/tracking/')) {
        return
    }
    // 成功请求已有服务端API_REQUEST观测，客户端仅补充业务失败与网络失败细节。
    if (resultStatus === 'SUCCESS') {
        return
    }
    const startedAt = config && config.__qcyyStartedAt
    trackEvent('API_RESULT', '后台接口调用', {
        requestUrl: url,
        method: config && config.method || 'post',
        resultStatus,
        errorCode: detail.errorCode || '',
        errorMessage: detail.errorMessage || '',
        durationMs: nowDuration(startedAt),
        hasPayload: Boolean(config && config.data),
    })
    flushTrackingEvents()
}

export function setTrackingActor(user) {
    currentUser = user || null
    recordSession()
}

export function initAdminTracking(router) {
    if (!router || window.__qcyyAdminTrackingInstalled) {
        return
    }
    window.__qcyyAdminTrackingInstalled = true
    installClickTracking()
    router.beforeEach((to, from, next) => {
        trackRouteLeave()
        next()
    })
    router.afterEach((to, from) => {
        trackRouteView(to, from)
    })
    recordSession()
    heartbeatTimer = window.setInterval(() => recordSession(), 60000)
    window.addEventListener('beforeunload', () => {
        trackRouteLeave()
        flushTrackingEvents(true)
        recordSession(true)
        if (heartbeatTimer) {
            window.clearInterval(heartbeatTimer)
        }
    })
}
